/**
 * API Service
 * Centralized API client for backend communication
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  StatementGenerationRequest,
  MultiMonthStatementResult
} from '../models/Statement';
import { StatementTemplate } from '../models/Template';
import { UploadedSample, ZipBatchResult, BatchProcessingStatus } from '../models/UploadedSample';

// API Base URL
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

/**
 * Create axios instance with default config
 */
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 300000, // 5 minutes for generation operations
    withCredentials: true, // Send cookies
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // Request interceptor
  instance.interceptors.request.use(
    (config) => {
      // Add any auth tokens here if needed
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error: AxiosError) => {
      // Handle 401 Unauthorized - redirect to login
      // BUT: Don't redirect if we're checking auth status (getCurrentUser)
      // or if we're already on login-related pages
      const requestUrl = error.config?.url || '';
      const isAuthCheck = requestUrl.includes('/auth/me');
      const isAuthEndpoint = requestUrl.includes('/auth/');
      const isAlreadyOnLogin = window.location.pathname === '/login' ||
                               window.location.pathname === '/forgot-password' ||
                               window.location.pathname === '/reset-password';

      if (error.response?.status === 401 && !isAuthCheck && !isAuthEndpoint && !isAlreadyOnLogin) {
        window.location.href = '/login';
      }

      // Transform error to friendly message
      const message = (error.response?.data as any)?.error || error.message || 'An error occurred';
      return Promise.reject(new Error(message));
    }
  );

  return instance;
};

const api = createAxiosInstance();

/**
 * Authentication API
 */
export const authAPI = {
  /**
   * Login
   */
  login: async (email: string, password: string): Promise<{ user: any }> => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  /**
   * Logout
   */
  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },
  /**
   * Get current user
   */
  getCurrentUser: async (): Promise<{ user: any }> => {
    const response = await api.get('/auth/me');
    return response.data;
  },


  /**
   * Forgot password
   */
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  /**
   * Reset password
   */
  resetPassword: async (token: string, newPassword: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    return response.data;
  }
};

/**
 * Templates API
 */
export const templatesAPI = {
  /**
   * Get all templates
   */
  getTemplates: async (): Promise<StatementTemplate[]> => {
    const response = await api.get('/templates');
    return response.data;
  },

  /**
   * Get template by ID
   */
  getTemplate: async (id: string): Promise<StatementTemplate> => {
    const response = await api.get(`/templates/${id}`);
    return response.data;
  },

  /**
   * Upload single sample
   */
  uploadSample: async (file: File, bankName: string): Promise<UploadedSample> => {
    const formData = new FormData();
    formData.append('sample', file);
    formData.append('bankName', bankName);

    const response = await api.post('/templates/upload-sample', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  /**
   * Process uploaded sample
   */
  processSample: async (sampleId: string): Promise<StatementTemplate> => {
    const response = await api.post('/templates/process-sample', { sampleId });
    return response.data;
  },

  /**
   * Upload batch ZIP
   */
  uploadBatch: async (file: File): Promise<{ batchId: string }> => {
    const formData = new FormData();
    formData.append('zip', file);

    const response = await api.post('/templates/upload-batch', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  /**
   * Get batch status
   */
  getBatchStatus: async (batchId: string): Promise<BatchProcessingStatus> => {
    const response = await api.get(`/templates/batches/${batchId}`);
    return response.data;
  },

  /**
   * Update template
   */
  updateTemplate: async (id: string, updates: Partial<StatementTemplate>): Promise<StatementTemplate> => {
    const response = await api.patch(`/templates/${id}`, updates);
    return response.data;
  },

  /**
   * Delete template
   */
  deleteTemplate: async (id: string): Promise<void> => {
    await api.delete(`/templates/${id}`);
  }
};

/**
 * Generation API
 */
export const generationAPI = {
  /**
   * Generate statements
   */
  generateStatements: async (request: StatementGenerationRequest): Promise<MultiMonthStatementResult> => {
    const response = await api.post('/generate-statements', request, {
      timeout: 600000 // 10 minutes for complex generation
    });
    return response.data;
  },

  /**
   * Download PDF
   */
  downloadPDF: async (monthIndex: number, result: any): Promise<Blob> => {
    // Check if result has pdfUrl in months (PDF template format)
    if (result.months && result.months[monthIndex]?.pdfUrl) {
      const pdfUrl = result.months[monthIndex].pdfUrl;
      // Use fetch for binary data to avoid axios transformation issues
      const fullUrl = API_BASE_URL.replace('/api', '') + pdfUrl;
      const response = await fetch(fullUrl, { credentials: 'include' });
      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }
      return await response.blob();
    }
    
    // Fall back to base64 format (original multi-month format)
    if (result.pdfPerMonth && result.pdfPerMonth[monthIndex]) {
      const base64 = result.pdfPerMonth[monthIndex];
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);

      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }

      return new Blob([bytes], { type: 'application/pdf' });
    }
    
    throw new Error('No PDF data available');
  }
};

/**
 * Admin API
 */
export const adminAPI = {
  /**
   * Get all users
   */
  getUsers: async (): Promise<any[]> => {
    const response = await api.get('/admin/users');
    return response.data;
  },

  /**
   * Get user by ID
   */
  getUser: async (id: string): Promise<any> => {
    const response = await api.get(`/admin/users/${id}`);
    return response.data;
  },

  /**
   * Create user
   */
  createUser: async (email: string, role: string): Promise<any> => {
    const response = await api.post('/admin/users', { email, role });
    return response.data;
  },

  /**
   * Update user
   */
  updateUser: async (id: string, updates: { isActive?: boolean; role?: string }): Promise<any> => {
    const response = await api.patch(`/admin/users/${id}`, updates);
    return response.data;
  },

  /**
   * Delete user
   */
  deleteUser: async (id: string): Promise<void> => {
    await api.delete(`/admin/users/${id}`);
  },

  /**
   * Update user credits
   */
  updateUserCredits: async (id: string, credits: number, action: "add" | "set"): Promise<any> => {
    const response = await api.patch("/admin/users/" + id + "/credits", { credits, action });
    return response.data;
  }
};

/**
 * Health check
 */
export const healthAPI = {
  check: async (): Promise<{ status: string; timestamp: string }> => {
    const response = await api.get('/health');
    return response.data;
  }
};

/**
 * PDF Template API
 * API for managing PDF templates with coordinate mapping
 */
export const pdfTemplateAPI = {
  /**
   * Get all PDF templates
   */
  getTemplates: async (): Promise<any[]> => {
    const response = await api.get('/pdf-templates');
    return response.data;
  },

  /**
   * Get PDF template by ID with mappings
   */
  getTemplate: async (id: string): Promise<any> => {
    const response = await api.get(`/pdf-templates/${id}`);
    return response.data;
  },

  /**
   * Upload new PDF template
   */
  uploadTemplate: async (file: File, bankName: string, templateName: string, description?: string): Promise<any> => {
    const formData = new FormData();
    formData.append('pdf', file);
    formData.append('bankName', bankName);
    formData.append('templateName', templateName);
    if (description) {
      formData.append('description', description);
    }

    const response = await api.post('/pdf-templates/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  /**
   * Update PDF template metadata
   */
  updateTemplate: async (id: string, updates: { templateName?: string; description?: string; isActive?: boolean }): Promise<any> => {
    const response = await api.patch(`/pdf-templates/${id}`, updates);
    return response.data;
  },

  /**
   * Delete PDF template
   */
  deleteTemplate: async (id: string): Promise<void> => {
    await api.delete(`/pdf-templates/${id}`);
  },

  /**
   * Get template preview URL
   */
  getPreviewUrl: (id: string): string => {
    return `${API_BASE_URL}/pdf-templates/${id}/preview`;
  },

  /**
   * Save field mappings for a template
   */
  saveMappings: async (templateId: string, mappings: any[]): Promise<any> => {
    const response = await api.put(`/pdf-templates/${templateId}/mappings`, { mappings });
    return response.data;
  },

  /**
   * Get mappings for a template
   */
  getMappings: async (templateId: string): Promise<any[]> => {
    const response = await api.get(`/pdf-templates/${templateId}/mappings`);
    return response.data;
  },

  /**
   * Get template usage statistics
   */
  getUsageStats: async (templateId: string): Promise<any> => {
    const response = await api.get(`/pdf-templates/${templateId}/stats`);
    return response.data;
  },

  /**
   * Duplicate a template
   */
  /**
   * Generate statement from PDF template
   */
  generateStatement: async (templateId: string, data: any): Promise<any> => {
    const response = await api.post(`/pdf-templates/${templateId}/generate`, data, {
      timeout: 600000 // 10 minutes for complex generation
    });
    return response.data;
  },

  /**
   * Duplicate a template
   */
  duplicateTemplate: async (id: string): Promise<any> => {
    const response = await api.post(`/pdf-templates/${id}/duplicate`);
    return response.data;
  }
};

export default {
  auth: authAPI,
  templates: templatesAPI,
  generation: generationAPI,
  admin: adminAPI,
  health: healthAPI,
  pdfTemplates: pdfTemplateAPI
};
